import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import TableSkeleton from "../../../../../../../../component/TableSkeleton";
import { Box, Stack, IconButton, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { useOutletContext } from "react-router";
import EmptyState from "../../../../../../../../component/EmptyState";
import MonthStrip, { MonthStripLegend, toneColors } from "../../../../../../../../component/attendance/MonthStrip";
import PunchCorrection from "../../../../../../../../component/attendance/PunchCorrection";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../../../../component/listing";
import { getAllDayAttendanceByEmployeeId } from "../../../../../../../../services/facialRecognition";
import {
  createAttendanceRequest, getAllAttendanceRequests,
} from "../../../../../../../../services/attendanceRequest";
import { getTokenInfo } from "../../../../../../../../services/api";
import { getShiftForEmployee } from "../../../../../../../../services/shift";
import { getAllHolidays } from "../../../../../../../../services/holiday";
import { getLeaveCalendar, getLeaveBalanceByEmployee, getAllLeaveType } from "../../../../../../../../services/leave";
import { buildAttendanceMonth, daysInMonth, toDayString } from "../../../../../../../../utils/attendanceMonth";
import { calculateTime } from "../../../../../../../../utils/calculateTime";
import { getDuration } from "../../../../../../../../utils/getDuration";
import { tokens, displayFont, radius } from "../../../../../../../../theme";

import { pressable } from "../../../../../../../../component/pressableProps";
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// days that aren't working days — recessed in the table so the worked days lead
const OFF_DAY_STATUSES = new Set(["WEEKLY_OFF", "HOLIDAY", "UNKNOWN", "NOT_TRACKED"]);

// Days whose punches an admin can correct. A correction has to land on a real
// working day with a resolvable shift — the API recomputes late/early against
// the shift's start and end, so days with no shift (UNKNOWN) or no tracking at
// all (NOT_TRACKED) have nothing to compute against. Leave, holidays and
// weekly offs aren't punch problems; changing those means changing the leave
// record or the calendar, not the day.
const CORRECTABLE_STATUSES = new Set(["MISSING_PUNCH", "ABSENT", "PRESENT", "LATE"]);

// an em dash at full text colour reads as content; this reads as "nothing here"
const Dash = () => (
  <Box component="span" aria-label="none" sx={{ color: tokens.textPlaceholder }}>
    —
  </Box>
);

/**
 * A punch time that can be corrected. Reads as plain text until hovered, so the
 * register stays a register rather than turning into a form — the affordance
 * appears where the reader is already looking, and an empty correctable cell
 * says "Add" instead of leaving them to guess a dash is clickable.
 */
const TimeCell = ({ value, editable, onEdit, label }) => {
  if (!editable) return value != null ? calculateTime(value) : <Dash />;
  return (
    <Box
      component="button"
      type="button"
      onClick={onEdit}
      aria-label={`Correct ${label}`}
      sx={{
        font: "inherit",
        fontVariantNumeric: "tabular-nums",
        color: value != null ? tokens.text : tokens.textMuted,
        background: "transparent",
        border: "1px solid transparent",
        borderRadius: "6px",
        px: "0.5rem",
        py: "0.25rem",
        ml: "-0.375rem",
        cursor: "pointer",
        transition: "background-color 120ms cubic-bezier(0.2, 0, 0.2, 1), border-color 120ms cubic-bezier(0.2, 0, 0.2, 1)",
        "&:hover": { background: tokens.surfaceSubtle, borderColor: tokens.border },
        "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "1px" },
      }}
    >
      {value != null ? calculateTime(value) : "Add"}
    </Box>
  );
};

/**
 * Who changed this day and why. The employee's own request and a supervisor's
 * assertion about them are different kinds of claim, so the note names which
 * one it is rather than flattening both to "corrected".
 */
const CorrectionNote = ({ correction }) => {
  const by = correction.raisedBy;
  const name = by?.fname ? `${by.fname} ${by.lname || ""}`.trim() : null;
  const attested = correction.source && correction.source !== "SELF";
  return (
    <Box sx={{ fontSize: "0.8125rem", lineHeight: 1.5 }}>
      <Box component="span" sx={{ color: tokens.text }}>{correction.reason}</Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
        {attested ? "Attested" : "Requested"}
        {name ? ` by ${name}` : ""}
        {correction.bypassedPolicy?.length
          ? ` · outside ${correction.bypassedPolicy.join(" and ")} policy`
          : ""}
      </Box>
    </Box>
  );
};

const SummaryTile = ({ label, value, tone, active, onClick }) => {
  const { fg, bg } = toneColors(tone);
  const clickable = Boolean(onClick);
  const empty = !value;
  return (
    <Box
      component={clickable ? "button" : "div"}
      type={clickable ? "button" : undefined}
      {...pressable(onClick)}
      aria-pressed={clickable ? active : undefined}
      title={clickable ? `Show only ${label.toLowerCase()} days` : undefined}
      sx={{
        flex: "1 1 6.5rem",
        minWidth: "6.5rem",
        textAlign: "left",
        font: "inherit",
        background: active ? bg : tokens.card,
        border: `1px solid ${active ? fg : tokens.border}`,
        borderRadius: "12px",
        p: "0.75rem 1rem",
        cursor: clickable ? "pointer" : "default",
        transition: "border-color 120ms cubic-bezier(0.2, 0, 0.2, 1), background-color 120ms cubic-bezier(0.2, 0, 0.2, 1)",
        "&:hover": clickable ? { borderColor: fg, background: bg } : undefined,
        "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
      }}
    >
      <Box
        sx={{
          fontFamily: displayFont, fontWeight: 600, fontSize: "1.25rem",
          fontWeight: 600,
          lineHeight: 1.2,
          fontVariantNumeric: "tabular-nums",
          color: empty ? tokens.border : fg,
        }}
      >
        {value}
      </Box>
      <Box sx={{ fontSize: "0.8125rem", color: empty ? tokens.textMuted : tokens.text }}>
        {label}
      </Box>
    </Box>
  );
};

const ProfileAttendance = () => {
  const [, employeeId] = useOutletContext();
  const today = useMemo(() => new Date(), []);
  // an admin can reach their own profile through this same tab
  const isOwnRegister =
    String(getTokenInfo()?.accessTokenData?.employeeId || "") === String(employeeId || "");

  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [records, setRecords] = useState([]);
  const [shift, setShift] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [leave, setLeave] = useState([]);
  const [balances, setBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [corrections, setCorrections] = useState([]);
  // which cell the correction popover hangs off, and the day it is editing
  const [editing, setEditing] = useState({ anchorEl: null, dayString: null });

  const rowRefs = useRef({});
  // stepping through months fires overlapping fetches; only the newest may write
  const requestId = useRef(0);

  const { year, month } = cursor;
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth();

  // `quiet` refetches without tearing the page down to a loader. Saving a
  // correction has to reload the month, but swapping the table for a spinner
  // unmounts the editor mid-save and drops the admin's place in a long register.
  const fetchMonth = useCallback(async ({ quiet = false } = {}) => {
    if (!employeeId) return;
    const id = requestId.current + 1;
    requestId.current = id;
    if (!quiet) setLoading(true);
    setFailed(false);
    const startDate = toDayString(year, month, 1);
    const endDate = toDayString(year, month, daysInMonth(year, month));
    const [attendance, shiftRes, holidayRes, leaveRes, balanceRes, typeRes, correctionRes] = await Promise.all([
      getAllDayAttendanceByEmployeeId(employeeId, { startDate, endDate }),
      getShiftForEmployee(employeeId, startDate),
      getAllHolidays(),
      getLeaveCalendar({ from: startDate, to: endDate, employee: employeeId, status: "Approved" }),
      getLeaveBalanceByEmployee(employeeId),
      getAllLeaveType(),
      getAllAttendanceRequests({ type: "REGULARIZATION", employeeId }),
    ]);
    if (id !== requestId.current) return; // a later month already won
    // only attendance failing is worth an error state; the rest degrade to "no
    // holidays / no leave / no balances", which render correctly as absent data
    if (attendance?.success === false) setFailed(true);
    setRecords(Array.isArray(attendance?.data) ? attendance.data : []);
    // no shift assigned is a valid state — the month just renders without weekly offs
    setShift(shiftRes?.data || null);
    setHolidays(Array.isArray(holidayRes?.data) ? holidayRes.data : []);
    setLeave(Array.isArray(leaveRes?.data) ? leaveRes.data : []);
    setBalances(Array.isArray(balanceRes?.data) ? balanceRes.data : []);
    setLeaveTypes(Array.isArray(typeRes?.data) ? typeRes.data : []);
    setCorrections(Array.isArray(correctionRes?.data) ? correctionRes.data : []);
    setLoading(false);
  }, [employeeId, year, month]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  // a new month invalidates any day/status selection from the previous one
  useEffect(() => {
    setSelectedDay(null);
    setStatusFilter(null);
  }, [year, month]);

  const { days, summary } = useMemo(
    () => buildAttendanceMonth({ year, month, records, shift, holidays, leave, today }),
    [year, month, records, shift, holidays, leave, today]
  );

  // balances arrive as { leaveType: <id>, quantity }; label them via the type list
  const namedBalances = useMemo(() => {
    const byId = new Map(leaveTypes.map((t) => [String(t._id), t]));
    return balances
      .map((b) => {
        const type = byId.get(String(b.leaveType));
        return type ? { code: type.leaveCode || type.name, quantity: b.quantity } : null;
      })
      .filter(Boolean);
  }, [balances, leaveTypes]);

  // Applied corrections, newest first, keyed by day. Only approved ones are
  // shown against a day: a pending request hasn't changed the record yet, and
  // captioning an unchanged row as corrected would be a lie.
  const correctionByDay = useMemo(() => {
    const map = new Map();
    [...corrections]
      .filter((c) => c.status === "Approved" && c.dayString)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .forEach((c) => map.set(c.dayString, c));
    return map;
  }, [corrections]);

  const editingDay = useMemo(
    () => days.find((d) => d.dayString === editing.dayString) || null,
    [days, editing.dayString]
  );

  const openEditor = (event, dayString) => {
    event.stopPropagation(); // the row's own click toggles selection
    setEditing({ anchorEl: event.currentTarget, dayString });
  };
  const closeEditor = () => setEditing({ anchorEl: null, dayString: null });

  const submitCorrection = async (payload) => {
    const res = await createAttendanceRequest({
      type: "REGULARIZATION",
      dayString: editing.dayString,
      // Nobody attests for themselves — that is the audit hole the whole split
      // exists to close. On your own register this is an ordinary request,
      // subject to the policy's window, cap and approval like any other; on
      // someone else's, naming them is what makes it an attestation.
      ...(isOwnRegister ? {} : { employeeId, source: "SUPERVISOR_ATTESTED" }),
      ...payload,
    });
    if (res?.success) {
      // dismiss before reloading: the editor reseeds its fields from the day it
      // is pointed at, so leaving it open through a refetch would repopulate it
      // with the freshly-corrected values and read as though nothing was saved
      closeEditor();
      await fetchMonth({ quiet: true });
    }
    return res;
  };

  // the strip covers the whole month; the table only lists days that have resolved
  const tableDays = useMemo(() => {
    const settled = days.filter((d) => d.status.key !== "UPCOMING");
    return statusFilter ? settled.filter((d) => d.status.key === statusFilter) : settled;
  }, [days, statusFilter]);

  useEffect(() => {
    if (!selectedDay) return;
    rowRefs.current[selectedDay]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [selectedDay, tableDays]);

  const step = (delta) => {
    setCursor((c) => {
      const next = new Date(Date.UTC(c.year, c.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });
  };

  const toggleFilter = (key) => {
    setStatusFilter((f) => (f === key ? null : key));
    setSelectedDay(null);
  };

  const shiftLabel = shift
    ? `${shift.name}${
        shift.days?.[1]?.startTime != null
          ? ` · ${calculateTime(shift.days[1].startTime)}–${calculateTime(shift.days[1].endTime)}`
          : ""
      }`
    : null;

  const notice = !summary.shiftKnown
    ? {
        title: "No shift assigned.",
        body:
          "Working days and weekly offs are unknown, so days without a punch are left " +
          "unevaluated rather than counted as absent. Assign a shift to complete this register.",
      }
    : !summary.hasAnyRecord
    ? {
        title: `No attendance recorded in ${MONTHS[month]} ${year}.`,
        body:
          "Not a single punch was captured this month, so these days are reported as not " +
          "tracked rather than absent. Check that this employee is enrolled in attendance " +
          "before treating the month as time off.",
      }
    : null;

  // Counts worth a tile. "Absent" needs a roster and evidence of tracking; where
  // either is missing it is replaced by the honest count rather than a false zero.
  const tiles = [
    { key: "PRESENT", label: "Present", value: summary.present, tone: "success" },
    { key: "LATE", label: "Late", value: summary.late, tone: "warning" },
    !summary.shiftKnown
      ? { key: "UNKNOWN", label: "Not evaluated", value: summary.unknown, tone: "muted" }
      : !summary.hasAnyRecord
      ? { key: "NOT_TRACKED", label: "Not tracked", value: summary.notTracked, tone: "muted" }
      : { key: "ABSENT", label: "Absent", value: summary.absent, tone: "danger" },
    { key: "ON_LEAVE", label: "On leave", value: summary.onLeave, tone: "info" },
    { key: "MISSING_PUNCH", label: "Missing punch", value: summary.missingPunch, tone: "violet" },
    { key: "HOLIDAY", label: "Holiday", value: summary.holiday, tone: "muted" },
    { key: "WEEKLY_OFF", label: "Weekly off", value: summary.weeklyOff, tone: "muted" },
  ];

  return (
    <Box>
      {/* month scope + shift context */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton aria-label="Previous" size="small" onClick={() => step(-1)} sx={{ border: `1px solid ${tokens.border}` }}>
            <ChevronLeftIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
          <Box sx={{ fontSize: "1rem", fontWeight: 600, minWidth: "9rem", textAlign: "center" }}>
            {MONTHS[month]} {year}
          </Box>
          <IconButton aria-label="Next"
            size="small"
            onClick={() => step(1)}
            disabled={isCurrentMonth}
            sx={{ border: `1px solid ${tokens.border}` }}
          >
            <ChevronRightIcon sx={{ fontSize: "1.125rem" }} />
          </IconButton>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
          {shiftLabel && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{
                border: `1px solid ${tokens.border}`,
                borderRadius: "999px",
                background: tokens.card,
                px: 1.5,
                height: "2rem",
              }}
            >
              <ScheduleIcon sx={{ fontSize: "1rem", color: tokens.textMuted }} />
              <Box sx={{ fontSize: "0.8125rem", color: tokens.text }}>{shiftLabel}</Box>
            </Stack>
          )}
          {namedBalances.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                border: `1px solid ${tokens.border}`,
                borderRadius: "999px",
                background: tokens.card,
                px: 1.5,
                height: "2rem",
              }}
            >
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Leave left</Box>
              {namedBalances.map((b) => (
                <Box key={b.code} sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>
                  {b.code}{" "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 600, color: tokens.text, fontVariantNumeric: "tabular-nums" }}
                  >
                    {b.quantity}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* Neither case can support the word "absent", and saying so beats
          silently reporting a month the employee never had a chance to fail. */}
      {!loading && !failed && notice && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "flex-start",
            border: `1px solid ${tokens.warning}`,
            background: tokens.warningTint,
            borderRadius: "12px",
            p: "0.75rem 1rem",
            mb: 2,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.125rem", color: tokens.warningText, mt: "4px" }} />
          <Box sx={{ fontSize: "0.8125rem", color: tokens.text, lineHeight: 1.5 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>{notice.title}</Box>{" "}
            {notice.body}
          </Box>
        </Stack>
      )}

      {loading ? (
          /* The header and the column widths are known before the data
             is, so they render immediately and nothing shifts when the
             rows land. */
          <Table sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: "6.5rem" }}>Date</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "10rem" }}>Status</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "5rem" }} align="right">In</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "5rem" }} align="right">Out</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "6.5rem" }} align="right">Hours</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "6rem" }} align="right">Late by</TableCell>
                  <TableCell sx={headCellSx}>Remarks</TableCell>
                </TableRow>
              </TableHead>
            <TableBody><TableSkeleton columns={7} /></TableBody>
          </Table>
        ) : failed ? (
        <EmptyState variant="error" title="Couldn't load attendance" onRetry={() => fetchMonth()} />
      ) : (
        <>
          <Stack
            direction="row"
            sx={{ gap: 2, alignItems: "stretch", flexWrap: "wrap", mb: 2 }}
          >
            <Stack direction="row" sx={{ gap: 1, flexWrap: "wrap", flex: "1 1 30rem" }}>
              {tiles.map((t) => (
                <SummaryTile
                  key={t.key}
                  label={t.label}
                  value={t.value}
                  tone={t.tone}
                  active={statusFilter === t.key}
                  onClick={t.value > 0 ? () => toggleFilter(t.key) : undefined}
                />
              ))}
            </Stack>
            {/* hours are a total, not a bucket you can filter the table down to */}
            <Box
              sx={{
                background: tokens.card,
                border: `1px solid ${tokens.border}`,
                borderRadius: `${radius.card}px`,
                p: "0.75rem 1rem",
                minWidth: "8.5rem",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              <Box
                sx={{
                  fontFamily: displayFont, fontWeight: 600, fontSize: "1.25rem",
                  fontWeight: 600,
                  color: summary.totalHours ? tokens.text : tokens.textMuted,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {summary.totalHours ? getDuration(summary.totalHours) : "—"}
              </Box>
              <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>Hours worked</Box>
            </Box>
          </Stack>

          <Box
            sx={{
              border: `1px solid ${tokens.border}`,
              borderRadius: `${radius.card}px`,
              p: "1.25rem 1.25rem 1rem",
              mb: 2,
              background: tokens.card,
            }}
          >
            <MonthStrip
              days={days}
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
              todayString={toDayString(today.getFullYear(), today.getMonth(), today.getDate())}
            />
            <MonthStripLegend days={days} />
          </Box>

          {tableDays.length === 0 ? (
            <EmptyState
              title={statusFilter ? "No matching days" : "No attendance records"}
              description={
                statusFilter
                  ? "No days in this month match that status."
                  : `This employee has no recorded attendance for ${MONTHS[month]} ${year}.`
              }
              actionLabel={statusFilter ? "Clear filter" : undefined}
              onAction={statusFilter ? () => setStatusFilter(null) : undefined}
            />
          ) : (
            <Table sx={{ width: "100%", tableLayout: "fixed" }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: "6.5rem" }}>Date</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "10rem" }}>Status</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "5rem" }} align="right">In</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "5rem" }} align="right">Out</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "6.5rem" }} align="right">Hours</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "6rem" }} align="right">Late by</TableCell>
                  <TableCell sx={headCellSx}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableDays.map((d) => {
                  const isSelected = selectedDay === d.dayString;
                  // non-working days recede so the worked days carry the scan
                  const isOffDay = OFF_DAY_STATUSES.has(d.status.key);
                  const numeric = { ...bodyCellSx, fontVariantNumeric: "tabular-nums" };
                  const correctable = CORRECTABLE_STATUSES.has(d.status.key);
                  const correction = correctionByDay.get(d.dayString);
                  return (
                    <TableRow
                      key={d.dayString}
                      ref={(el) => { rowRefs.current[d.dayString] = el; }}
                      hover
                      onClick={() => setSelectedDay(isSelected ? null : d.dayString)}
                      sx={{
                        cursor: "pointer",
                        background: isSelected
                          ? `${tokens.accentTint}`
                          : isOffDay
                          ? tokens.surface
                          : "transparent",
                        boxShadow: isSelected ? `inset 2px 0 0 ${tokens.accent}` : "none",
                        "&:hover": { background: isSelected ? `${tokens.accentTint}` : tokens.surfaceSubtle },
                      }}
                    >
                      <TableCell sx={{ ...bodyCellSx, whiteSpace: "nowrap" }}>
                        <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                          {String(d.dayNum).padStart(2, "0")} {MONTHS[month].slice(0, 3)}
                        </Box>{" "}
                        <Box component="span" sx={{ color: tokens.textMuted, fontSize: "0.8125rem" }}>
                          {d.weekday}
                        </Box>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <StatusChip
                            label={d.status.label}
                            color={toneColors(d.status.tone).fg}
                            bg={toneColors(d.status.tone).bg}
                          />
                          {/* the code only earns space when it adds something the label doesn't */}
                          {d.code !== d.status.code && (
                            <Box
                              component="span"
                              sx={{ fontSize: "0.8125rem", fontWeight: 600, color: toneColors(d.status.tone).fg }}
                            >
                              {d.code}
                            </Box>
                          )}
                          {/* same mark as the strip, so the two readings of the
                              month agree on which days were asserted */}
                          {d.corrected && (
                            <Box
                              component="span"
                              title="Punch corrected"
                              sx={{ fontSize: "0.875rem", fontWeight: 600, color: tokens.textMuted, lineHeight: 1.6 }}
                            >
                              *
                            </Box>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={numeric} align="right">
                        <TimeCell
                          value={d.arrivalTime}
                          editable={correctable}
                          onEdit={(e) => openEditor(e, d.dayString)}
                          label={`check-in on ${d.dayNum} ${MONTHS[month].slice(0, 3)}`}
                        />
                      </TableCell>
                      <TableCell sx={numeric} align="right">
                        <TimeCell
                          value={d.departureTime}
                          editable={correctable}
                          onEdit={(e) => openEditor(e, d.dayString)}
                          label={`check-out on ${d.dayNum} ${MONTHS[month].slice(0, 3)}`}
                        />
                      </TableCell>
                      <TableCell sx={numeric} align="right">
                        {d.totalHours ? getDuration(d.totalHours) : <Dash />}
                      </TableCell>
                      <TableCell
                        sx={{ ...numeric, color: d.lateBy ? tokens.warningText : tokens.text }}
                        align="right"
                      >
                        {d.lateBy ? getDuration(d.lateBy) : <Dash />}
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, color: tokens.textMuted }}>
                        {correction ? (
                          <CorrectionNote correction={correction} />
                        ) : (
                          d.remark || <Dash />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

        </>
      )}

      {/* Outside the loading branch on purpose: a save refetches the month, and
          if the editor lived in there it would unmount mid-submit and lose the
          in-flight state along with its anchor. */}
      <PunchCorrection
        anchorEl={editing.anchorEl}
        day={editingDay}
        dateLabel={
          editingDay
            ? `${String(editingDay.dayNum).padStart(2, "0")} ${MONTHS[month]} ${year} · ${editingDay.weekday}`
            : ""
        }
        onClose={closeEditor}
        onSubmit={submitCorrection}
        isOwnRegister={isOwnRegister}
      />
    </Box>
  );
};

export default ProfileAttendance;
